
import React from 'react';
import { Monitor, Bike, Truck, Gauge, CheckCircle2, Clock, Home } from 'lucide-react';
import { Order, Driver, SystemConfig } from '../../types';
import { getFleetPerformanceIndex } from '../../services/logistics/fleet/telemetry';
import { calculateDriverMinutesToHub } from '../../services/logistics/fleet/metrics';
import { formatETA } from '../../services/logistics/core/timing';
import { OrderMenu } from '../order/OrderMenu';

const formatClientName = (name: string, isCompact: boolean) => {
  if (!name) return 'Cliente';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];


  const limit = isCompact ? 2 : 3;
  const namesToShow = parts.slice(0, limit);

  if (parts.length > limit) {
    const lastIndex = namesToShow.length - 1;
    namesToShow[lastIndex] = namesToShow[lastIndex].charAt(0) + '.';
  }

  return namesToShow.join(' ');
};
interface Props {
  assignedOrders: Order[];
  thirdPartyOrders: Order[];
  otherOrders: Order[];
  drivers: Driver[];
  config: SystemConfig;
  onRecallOrder: (orderId: string) => void;
  onReportIncident: (order: Order) => void;
  onManualComplete: (orderId: string) => void;
}

export const OperationalMonitor: React.FC<Props> = ({
  assignedOrders, thirdPartyOrders, otherOrders, drivers, config, onRecallOrder, onReportIncident, onManualComplete
}) => {
  const activeDrivers = drivers.filter(d => d.status !== 'inactive');
  const fleetPerformance = getFleetPerformanceIndex(drivers);

  return (
    <div className="hidden lg:flex flex-col bg-app-bg border-l border-border-dim w-full md:w-[260px] xl:w-[280px] 2xl:w-[300px] shrink-0">
      <div className="flex-1 flex flex-col overflow-hidden bg-sal-gunmetal-light/5">
          <div className="h-12 px-3 xl:px-4 flex justify-between items-center border-b border-border-dim bg-app-bg shrink-0">
            <h3 className="flex items-center gap-2 font-bold text-text-primary text-sm uppercase-ops">
              <Monitor className="w-4 h-4 text-sal-cyber-teal" /> MONITORAMENTO
            </h3>
            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg border ${fleetPerformance < 0.95 ? 'bg-status-critical/10 border-status-critical/30 text-status-critical' : 'bg-status-ready/10 border-status-ready/30 text-status-ready'}`}>
              <Gauge className="w-3 h-3" />
              <span className="text-[10px] font-black uppercase-ops">{fleetPerformance.toFixed(2)}x</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-4 no-scrollbar">
            <div className="space-y-2">
              <h4 className="text-[10px] font-black text-sal-solid-indigo uppercase-ops flex items-center gap-2 px-1">
                <Bike className="w-3 h-3" /> Frota Ativa
              </h4>

              {activeDrivers.map(driver => {
                const driverBag = assignedOrders.filter(o => o.driverId === driver.id);
                if (driverBag.length === 0 && driver.status === 'inactive') return null;
                const returnTime = calculateDriverMinutesToHub(driver, driverBag, config.businessLat, config.businessLng, config.fleetConfig.serviceTimeMinutes);

                return (
                  <div key={driver.id} className={`bg-card-bg border rounded-xl p-1.5 xl:p-2 transition-all ${driverBag.length > 0 ? 'border-sal-solid-indigo/30 shadow-md ring-1 ring-sal-solid-indigo/5' : 'border-border-dim opacity-50'}`}>
                    <div className="flex justify-between items-center mb-1 xl:mb-1.5">
                      <div className="flex items-center gap-1.5 xl:gap-2 min-w-0">
                        <div className="w-4 h-4 xl:w-5 xl:h-5 rounded bg-sal-solid-indigo flex items-center justify-center text-[9px] xl:text-[10px] font-bold text-badge-text-inv uppercase-ops shadow-sm shrink-0">{driver.name.charAt(0)}</div>
                        <span className="text-[11px] xl:text-xs font-bold text-text-primary uppercase-ops truncate">{driver.name}</span>
                      </div>
                      {driverBag.length > 0 && (
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded uppercase-ops bg-sal-solid-indigo/10 text-sal-solid-indigo flex items-center gap-1" title={`Volta em ${returnTime} minutos`}>
                          <Home className="w-2.5 h-2.5" />{formatETA(returnTime)}
                        </span>
                      )}
                    </div>

                    {driverBag.length > 0 && (
                      <div className="space-y-1.5 mt-2 pl-1 border-l border-indigo-500/20 ml-2.5">
                        {driverBag.map(order => {
                          const isDelivered = order.status === 'delivered';
                          // v41.7: ID Humano Correto no Monitor
                          const mainId = order.displayId ? `#${order.displayId}` : (order.shortCode ? `#${order.shortCode}` : order.id.slice(-4));
                          const secondaryId = order.shortCode && order.shortCode !== order.displayId ? ` (iF #${order.shortCode})` : '';

                          return (
                            <div key={order.id} className={`group relative flex flex-col gap-1 transition-opacity ${isDelivered ? 'opacity-60' : 'opacity-100'}`}>
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2 min-w-0">
                                  {isDelivered ? <CheckCircle2 className="w-3 h-3 text-status-ready shrink-0" /> : <Clock className="w-3 h-3 text-sal-solid-indigo shrink-0 animate-spin" />}
                                  <span className={`text-[10px] font-bold truncate max-w-[100px] xl:max-w-[120px] 2xl:max-w-[140px] ${isDelivered ? 'text-status-ready line-through' : 'text-text-secondary'}`}>
                                    <span className="uppercase-ops">{mainId}{secondaryId}</span> • {formatClientName(order.customerName, true)}
                                  </span>
                                </div>
                                <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <OrderMenu order={order} isReturned={false} isConfirmingDelete={false} onDeleteClick={() => { }} onManualComplete={onManualComplete} onReportIncident={onReportIncident} onRecall={onRecallOrder} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-2">
              <h4 className="text-[10px] font-black text-sal-burnt-orange uppercase-ops flex items-center gap-2 px-1">
                <Truck className="w-3 h-3" /> Rede Externa
              </h4>
              <div className="space-y-1.5">
                {thirdPartyOrders.length > 0 ? thirdPartyOrders.map(order => {
                  const isDelivered = order.status === 'delivered';
                  // v41.7: ID Humano Correto no Monitor
                  const mainId = order.displayId ? `#${order.displayId}` : (order.shortCode ? `#${order.shortCode}` : order.id.slice(-4));
                  const secondaryId = order.shortCode && order.shortCode !== order.displayId ? ` (iF #${order.shortCode})` : '';

                  return (
                    <div key={order.id} className={`flex justify-between items-center p-2 rounded-xl border transition-all ${isDelivered ? 'bg-status-ready/5 border-status-ready/20 opacity-60' : 'bg-card-bg border-border-dim group hover:border-sal-burnt-orange/30'}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        {isDelivered ? <CheckCircle2 className="w-3.5 h-3.5 text-status-ready" /> : <div className="w-2 h-2 bg-sal-burnt-orange rounded-full animate-pulse" />}
                        <span className={`text-[10px] font-bold truncate max-w-[80px] xl:max-w-[100px] xl:max-w-[140px] 2xl:max-w-[180px] ${isDelivered ? 'text-status-ready line-through' : 'text-text-primary'}`}>
                          <span className="uppercase-ops">{mainId}{secondaryId}</span> • {formatClientName(order.customerName, true)}
                        </span>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <OrderMenu order={order} isReturned={false} isConfirmingDelete={false} onDeleteClick={() => { }} onManualComplete={onManualComplete} onReportIncident={onReportIncident} onRecall={onRecallOrder} />
                      </div>
                    </div>
                  );
                }) : (
                  <div className="py-6 flex flex-col items-center justify-center opacity-20 border border-dashed border-border-dim rounded-2xl"><Truck className="w-6 h-6 mb-1" /><span className="text-[10px] font-black uppercase-ops">Vazio</span></div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-[10px] font-black text-amber-500 uppercase-ops flex items-center gap-2 px-1">
                <Monitor className="w-3 h-3" /> Outros Envios
              </h4>
              <div className="space-y-1.5">
                {otherOrders.length > 0 ? otherOrders.map(order => {
                  const isDelivered = order.status === 'delivered';
                  const mainId = order.displayId ? `#${order.displayId}` : (order.shortCode ? `#${order.shortCode}` : order.id.slice(-4));
                  const secondaryId = order.shortCode && order.shortCode !== order.displayId ? ` (iF #${order.shortCode})` : '';

                  return (
                    <div key={order.id} className={`flex justify-between items-center p-2 rounded-xl border transition-all ${isDelivered ? 'bg-status-ready/5 border-status-ready/20 opacity-60' : 'bg-card-bg border-border-dim group hover:border-amber-300/30'}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        {isDelivered ? <CheckCircle2 className="w-3.5 h-3.5 text-status-ready" /> : <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />}
                        <span className={`text-[10px] font-bold truncate max-w-[80px] xl:max-w-[100px] xl:max-w-[140px] 2xl:max-w-[180px] ${isDelivered ? 'text-status-ready line-through' : 'text-text-primary'}`}>
                          <span className="uppercase-ops">{mainId}{secondaryId}</span> • {formatClientName(order.customerName, true)}
                        </span>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <OrderMenu order={order} isReturned={false} isConfirmingDelete={false} onDeleteClick={() => { }} onManualComplete={onManualComplete} onReportIncident={onReportIncident} onRecall={onRecallOrder} />
                      </div>
                    </div>
                  );
                }) : (
                  <div className="py-6 flex flex-col items-center justify-center opacity-20 border border-dashed border-border-dim rounded-2xl"><Monitor className="w-6 h-6 mb-1" /><span className="text-[10px] font-black uppercase-ops">Vazio</span></div>
                )}
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};
